// Catalog.ID Client — Kit Introduction Page
// ============================================================================
// Page 2 of the kit (page 1 is the cover): what Catalog.ID is, what the kit
// contains — an annotated table of contents — and how to store it. Carries NO
// secret material and, since the product owner's review of 2026-09-08, NO
// account header either: the cover names the account, the domain and the
// version, and this page is text only.
//
// Contents list (product owner, 2026-09-08, after two rejected shapes): the
// classic form. The title stands at the left margin, a dotted leader runs to
// the START page at the right margin — never a range; the agreement "is at
// page 9" — and the description, where a page has one, follows on its own
// line at the left margin in the small note style, at most ONE line
// (TOC_DESCRIPTION_MAX_CHARS, pinned by test); anything longer belongs on the
// sheet itself or in the running text below the list. The recovery sheets are
// listed one per row — a custodian handed "sheet B" finds "page 7" — and share
// one description after the last of them; the extra copies follow as ONE row
// naming them by sheet ("Copies for custodians: A, B, C ×2", 2026-10-15 — a
// row per copy outgrew the page once every sheet got one) with a dash where
// the page would be, since they ride after the set, unnumbered (round 4), and
// end with one line saying so.
//
// pdfmake has no leader primitive. Each entry is therefore its own one-row
// table — title (auto) | leader (*) | page (auto) — whose middle cell alone
// carries a dotted bottom border: per-entry tables so that `auto` sizes the
// title cell to THAT title rather than to the longest one in the list. The
// dots sit at the row's bottom, ~2 pt under the baseline; the row has no
// bottom padding so they read as leaders, not as an underline.
//
// All copy lives in the exported constants so content edits touch no layout
// logic. Earlier reviews: users do not know that everything in Catalog is
// encrypted, so say so up front (2026-09-02). The reach of a recovery and the
// reissue duty, which used to sit in the recovery entry's eight-line
// description, now live in the storage guidance (2026-09-08).
//
// PAGE BUDGET: this page must stay ONE A4 page (the assembler's
// one-page-per-builder contract). Dropping the header and the long
// descriptions paid for the per-sheet and per-copy rows several times over;
// re-run the sample renderer after any change to this page or its copy.
// Round 8 (2026-09-09) added the Authenticator Key row and its description —
// ~31 pt — which pushed the consent statement onto a second page on the
// personal kit (the tightest of the three). Paid for WITHOUT touching the copy,
// by the paragraph and section margins below: body paragraphs 6 → 5 pt, the
// page title 10 → 8, the "Contents" heading 8 → 6, the list's bottom margin
// 10 → 8 and the consent statement's top margin 4 → 2. Measured on a 120 dpi
// raster afterwards: the personal introduction's ink ended at ~91 % of the
// page — roughly one more line of room, and the next row that wanted a place
// here had to buy it from the copy.
//
// Round 10 (2026-09-14) added the Bitcash Wallet row and its description, and
// did exactly that: the margins were already at their floor, so the row was
// paid for out of the INTRO and the STORAGE copy, which each lost a paragraph
// break and about two lines' worth of repetition (the "printed once" and
// "generated on your own device" sentences said the same thing twice, and the
// reissue instruction was three lines for one sentence). No fact was dropped.
// Re-measured on a 120 dpi raster of the sample kits, as ink used out of the
// 661.9 pt text box: the personal introduction ends at 638.0 pt (96.4 %), the
// organization's at 513.2 (77.5 %) and the delegate's at 441.8 (66.7 %). The
// personal one is the binding variant and has TWO lines left; the next row that
// wants a place on this page has to buy it from the copy, again.

import type { Content, TableCell, TableLayout } from 'pdfmake/interfaces';
import { kitConsentStatement, type KitLayout } from './kit-layout';
import { BOOKLET_STYLES, isRecoverySheetTitle, ROBOTO_ASCENDER_PER_MILLE } from './shared';

export const FRONT_PAGE_INTRO: string[] = [
    'Catalog.ID is your self-sovereign identity for the Catalog network. '
        + 'Everything stored in Catalog — claims, messages, files — is encrypted, '
        + 'and only the keys of the account holder can open it. Whoever holds those '
        + 'keys holds the account and any linked XRPL accounts; without them nothing '
        + 'can be read, not even by the operator.',
    'Every key protecting this identity was generated on your own device during '
        + 'registration, and this kit is the only physical copy of the credentials for '
        + 'the account named on the following pages: the operator never receives your '
        + 'secrets, and nothing printed here is stored anywhere else. If these pages are '
        + 'lost and no device still holds the keys, the identity cannot be restored by '
        + 'anyone — including the operator.',
];

/** The storage guidance. The first paragraph always prints; the other two
 *  only when the kit carries Account Recovery Sheets. */
export const FRONT_PAGE_STORAGE: string[] = [
    'Store this kit in a secure location, such as a safe or a safe-deposit box. '
        + 'Do not photograph it or scan it into cloud storage.',
    'The Account Recovery Sheets stay in the kit with the rest; hand out only the '
        + 'copies printed after it, one to each custodian named on it.',
    'A recovery hands over everything the identity\'s keys control, including any '
        + 'linked XRPL accounts; the people who combined the sheets then know it, so have '
        + 'the account issue a new supersecret and print a new set of sheets.',
];

/** Set once the public documentation exists; rendered as a final line. */
export const FRONT_PAGE_DOCS_URL: string | null = null;

// ── the contents list's descriptions ────────────────────────────────────────

/** The one-line budget: ~95 characters of 9 pt Roboto fit the description
 *  column (content width minus the number column), so 90 leaves margin. A
 *  description that needs more belongs on the sheet or in the text above. */
export const TOC_DESCRIPTION_MAX_CHARS = 90;

/** One line per section title. A title with no entry here prints without a
 *  description (the recovery sheets have their own, see below).
 *
 *  'Introduction' has none since round 8 (2026-09-09): the row pointed at the
 *  page the reader is already on, and describing it cost the page budget a line
 *  the Authenticator Key row needed. */
export const TOC_DESCRIPTIONS: Record<string, string> = {
    'Credentials': 'Your account id, password and supersecret, and the verified email and phone.',
    'Organization Credentials': 'The organization\'s supersecret, needed only for emergencies such as replacing its keys.',
    'Delegate Credentials': 'The delegate account you sign in with for the organization: id, password, supersecret.',
    'Account Unlock Sheet': 'Unlocks the account after a lock and can set a new password; each use needs a code.',
    'XRPL Master Key': 'The signing key for XRPL wallets this identity may create later; dormant until then.',
    'Authenticator Key': 'The shared secret authenticator apps turn into login codes; dormant until you turn it on.',
    'Bitcash Wallet': 'The wallet that came with this membership: its code, and the address others pay you at.',
    // The three agreements have NO description since round 10 (2026-09-14), for
    // the reason the Introduction row lost its own in round 8: the row already
    // reads "Catalog.ID Membership Agreement …… 9", and "the terms of Catalog.ID
    // membership, printed in full" adds nothing a reader did not just read. The
    // line paid for part of the Bitcash Wallet row (see PAGE BUDGET).
};

/** The recovery sheets' shared line, printed once under the last of them. */
export function recoverySheetsDescription(thresholdK: number, totalSharesN: number): string {
    return `Any ${thresholdK} of the ${totalSharesN} sheets recreate the supersecret. `
        + 'These stay; custodians get copies.';
}

/** Printed once under the copies row. */
export const COPY_SHEETS_DESCRIPTION =
    'Printed after the set, not numbered; each counts as the sheet it copies.';

/**
 * The ONE contents row for every copy (staging test 2026-10-15): the sheets in
 * the kit stay with it and custodians get copies, so a registration now prints
 * at least one copy of each sheet — and a row per copy outgrew this page. The
 * footer titles (`recoveryCopyTitles`) still name each copy on its own page;
 * this row counts them by sheet: "Copies for custodians: A, B, C ×2".
 */
export function copiesRowTitle(copyTitles: readonly string[]): string {
    const counts = new Map<string, number>();
    for (const title of copyTitles) {
        const sheet = title.split(' — ')[0];
        const letter = /Sheet ([A-Z]+)$/.exec(sheet)?.[1] ?? sheet;
        counts.set(letter, (counts.get(letter) ?? 0) + 1);
    }
    const list = [...counts].map(([letter, n]) => (n > 1 ? `${letter} ×${n}` : letter));
    return `Copies for custodians: ${list.join(', ')}`;
}

export interface FrontPageData {
    /** The kit's page plan — the contents rows, their numbers, the copies and
     *  the agreement all come from here. */
    layout: KitLayout;
    /** Present when the kit carries Account Recovery Sheets: the k-of-n rule
     *  their contents line states. Absent/null with recovery rows in the
     *  layout ⇒ the rows print without a description. */
    recoveryKit?: { thresholdK: number; totalSharesN: number } | null;
}

// ── the page budget's knobs (see the PAGE BUDGET note above) ───────────────
/** Space under each body paragraph. */
const PARAGRAPH_GAP = 4;
/** Space under the "Introduction" page title. */
const PAGE_TITLE_GAP = 6;
/** Space above the "Contents" heading. */
const CONTENTS_HEADING_GAP = 4;
/** Space under the contents list, before the storage guidance. */
const CONTENTS_LIST_GAP = 6;

export function buildFrontPage(data: FrontPageData): Content[] {
    const layout = data.layout;
    const content: Content[] = [
        { text: 'Introduction', style: 'pageTitle', margin: [0, 0, 0, PAGE_TITLE_GAP] },
    ];
    for (const para of FRONT_PAGE_INTRO) {
        content.push({ text: para, style: 'value', margin: [0, 0, 0, PARAGRAPH_GAP] });
    }

    // No bottom margin: the first entry's (lifted) top padding provides the gap.
    content.push({ text: 'Contents', style: 'section', margin: [0, CONTENTS_HEADING_GAP, 0, 0] });
    content.push(contentsList(layout, data.recoveryKit ?? null));

    // The custodian guidance and the recovery consequences only make sense
    // when the kit actually contains recovery sheets (a delegate kit does not).
    const hasRecoverySheets = layout.toc.some(e => isRecoverySheetTitle(e.title));
    for (const para of hasRecoverySheets ? FRONT_PAGE_STORAGE : FRONT_PAGE_STORAGE.slice(0, 1)) {
        content.push({ text: para, style: 'value', margin: [0, 0, 0, PARAGRAPH_GAP] });
    }

    const consent = kitConsentStatement(layout);
    if (consent) content.push({ text: consent, style: 'consent', margin: [0, 2, 0, 0] });

    if (FRONT_PAGE_DOCS_URL) {
        content.push({ text: `Full documentation: ${FRONT_PAGE_DOCS_URL}`, style: 'value', margin: [0, PARAGRAPH_GAP, 0, 0] });
    }
    return content;
}

// ── the contents list ───────────────────────────────────────────────────────

/** Roboto's descent below the baseline (pdfmake's bundled vfs copy: hhea
 *  descent 500, upem 2048), per 1000 of the font size; the ascent is shared
 *  with the footer geometry in shared.ts. */
const ROBOTO_DESCENDER_PER_MILLE = (500 * 1000) / 2048;

/** The entry rows print in the `value` style: its size and line height. */
const TOC_ENTRY_FONT_SIZE = 10;
const TOC_ENTRY_LINE_HEIGHT = (BOOKLET_STYLES['value'] as { lineHeight?: number }).lineHeight ?? 1;

/** Stroke width of the dotted leader. */
const TOC_LEADER_WIDTH = 0.8;

/** How far a `value` line's box extends BELOW its baseline. pdfmake draws
 *  the baseline one (unscaled) ascender under the line's top and puts the
 *  whole of the `lineHeight` leading underneath it — measured on a 300 dpi
 *  raster, 2026-09-08: a cell border at the row's bottom sat 4.8 pt below the
 *  baseline, not the 2.4 pt of the bare descender. */
const TOC_LINE_BELOW_BASELINE =
    ((ROBOTO_ASCENDER_PER_MILLE + ROBOTO_DESCENDER_PER_MILLE) / 1000) * TOC_ENTRY_FONT_SIZE * TOC_ENTRY_LINE_HEIGHT
    - (ROBOTO_ASCENDER_PER_MILLE / 1000) * TOC_ENTRY_FONT_SIZE;

/** Lift of the row's bottom edge, so the leader's stroke ENDS on the baseline
 *  (the stroke is drawn just below the edge): the title, the dots and the
 *  page number then share one line — the product owner's ask of 2026-09-08. */
const TOC_LEADER_LIFT = TOC_LINE_BELOW_BASELINE + TOC_LEADER_WIDTH;

/** Space between consecutive entries (the pre-lift 1 pt cell padding). */
const TOC_ROW_GAP = 1;

/** Clear space between the leader and the page number: a cell's border spans
 *  the cell's full width, padding included, so the gap has to live in the
 *  NEXT cell's left padding — otherwise the last dash can end right against
 *  the digit (owner, 2026-09-08: "half a dot right beside the number"). */
const TOC_LEADER_TO_PAGE_GAP = 6;

/** Clear space between the title and the leader's first dot. */
const TOC_TITLE_TO_LEADER_GAP = 5;

/** Layout of ONE entry's table: no rules except the dotted bottom border of
 *  the leader cell (index 1 = the single row's bottom line, and only the cell
 *  that asks for it — see `leaderCell`). The bottom padding is NEGATIVE by
 *  `TOC_LEADER_LIFT`, which pulls the row's bottom edge — and the dotted
 *  border on it — up onto the baseline; the top padding gives the same amount
 *  back, so the pitch from one entry to the next is exactly what a 1 pt
 *  padding gave before (pinned by test: top + bottom = TOC_ROW_GAP). */
const TOC_ENTRY_LAYOUT: TableLayout = {
    hLineWidth: (i: number) => (i === 1 ? TOC_LEADER_WIDTH : 0),
    vLineWidth: () => 0,
    hLineColor: () => '#000000',
    hLineStyle: () => ({ dash: { length: TOC_LEADER_WIDTH, space: 2.2 } }),
    paddingTop: () => TOC_ROW_GAP + TOC_LEADER_LIFT,
    paddingBottom: () => -TOC_LEADER_LIFT,
    paddingLeft: (i: number) => (i === 2 ? TOC_LEADER_TO_PAGE_GAP : 0),
    paddingRight: (i: number) => (i === 0 ? TOC_TITLE_TO_LEADER_GAP : 0),
};

/** Printed where the page number would be on a copy row — copies have none. */
const COPY_ROW_MARK = '—';

const NO_BORDER: [boolean, boolean, boolean, boolean] = [false, false, false, false];

function titleCell(text: string): TableCell {
    return { text, style: 'value', bold: true, border: NO_BORDER };
}

/** The empty middle cell: its bottom border is the dotted leader. */
function leaderCell(): TableCell {
    return { text: '', border: [false, false, false, true] };
}

function pageCell(text: string): TableCell {
    return { text, style: 'value', alignment: 'right', border: NO_BORDER };
}

/** "‹title› ……… ‹page›" — one row, one table, so `auto` fits this title. */
function entryRow(title: string, page: string): Content {
    return {
        layout: TOC_ENTRY_LAYOUT,
        table: { widths: ['auto', '*', 'auto'], body: [[titleCell(title), leaderCell(), pageCell(page)]] },
    };
}

/** A description follows its entry on its own line, at the left margin. Its
 *  top margin gives back the lift the entry row's negative padding took, so
 *  it sits under its title exactly as it would under an unlifted row. */
function descriptionLine(text: string): Content {
    return { text, style: 'note', margin: [0, TOC_ROW_GAP + TOC_LEADER_LIFT, 0, 0] };
}

function contentsList(layout: KitLayout, recoveryKit: { thresholdK: number; totalSharesN: number } | null): Content {
    const items: Content[] = [];
    const copyRows = (): void => {
        if (layout.copies.length === 0) return;
        items.push(entryRow(copiesRowTitle(layout.copies), COPY_ROW_MARK));
        items.push(descriptionLine(COPY_SHEETS_DESCRIPTION));
    };
    let copiesListed = false;

    layout.toc.forEach((section, i) => {
        // The START page only — a contents list does not print ranges.
        items.push(entryRow(section.title, String(section.firstPage)));
        if (isRecoverySheetTitle(section.title)) {
            const next = layout.toc[i + 1];
            const lastRecoverySheet = !next || !isRecoverySheetTitle(next.title);
            if (!lastRecoverySheet) return;
            if (recoveryKit) {
                items.push(descriptionLine(recoverySheetsDescription(recoveryKit.thresholdK, recoveryKit.totalSharesN)));
            }
            // The copies ride right after the master sheets and before the
            // appended agreement — list them where they are.
            copyRows();
            copiesListed = true;
            return;
        }
        const description = TOC_DESCRIPTIONS[section.title];
        if (description) items.push(descriptionLine(description));
    });
    if (!copiesListed) copyRows();

    return { stack: items, margin: [0, 0, 0, CONTENTS_LIST_GAP] };
}
